import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiCopy, FiX, FiCheck } from 'react-icons/fi';
import axios from 'axios';
import { handleUnauthorized } from '../../utils/apiClient';
import { ExtraDoc, getToolboxToken, getAmsToken } from './QuoteStepper';
import type { PdfDataSnapshot } from './GeneratedProposal';
import { useToast } from '../../context/ToastContext';
import '../../styles/QuoteProposal.css';

const svgBase = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

const FiDownload: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const FiSend: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const FiEdit2: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

/* ── Env URLs ── */


/* ── Props — flat (from QuoteStepper) ── */
export interface Step3Props {
  clientName: string;
  clientId: string;
  clientEmail: string;
  proposalTitle: string;
  transactionId: string;
  fileIds: string[];
  fileNames: string[];
  fileURL: string;
  selectedTemplate: string;
  selectedCustomTemplate: number;
  selectedCustomTemplateImages: string[];
  teamMember: string;
  proposalTeamMembers: any[];
  videoUrl: string;
  extraDocumentList: ExtraDoc[];
  customBlocks: any[];
  dynamicLinks?: { description: string; url: string }[];
  companyData: any;
  isDriveSelected?: boolean;
  selectedDriveData?: { folderId: string; fileIds: string[]; fileNames: string[]; folderIds: string[] };
  additionalSlotDrives?: { slotIndex: number; driveData: { folderId: string; fileIds: string[]; fileNames: string[]; folderIds: string[] } }[];
  selectedQuote: any;
  selectedPolicy?: any;
  selectedQuotesList?: any[];
  selectedPoliciesList?: any[];
  quoteCoverageDetails?: any[][];
  quoteDriverLists?: any[][];
  quoteVehicleLists?: any[][];
  coverageDetails: any[];
  driverList: any[];
  vehicleList: any[];
  onBack: () => void;
  onGenerating?: () => void;
  onGenerated?: () => void;
  onNewProposal?: () => void;
}

/* ── Build PDF blob using real data from GeneratedProposal ── */
async function buildPdfBlobFromSnapshot(
  pdfData: PdfDataSnapshot,
  selectedTemplate: string
): Promise<Blob> {
  const { pdf } = await import('@react-pdf/renderer');
  const templateNum = pdfData.selectedTemplate || parseInt(selectedTemplate, 10) || 3;

  // Build props for the PDF document using the real data snapshot
  const pdfProps = {
    title: pdfData.title,
    preparedFor: pdfData.preparedFor,
    preparedBy: pdfData.preparedBy,
    website: pdfData.website,
    logoURL: pdfData.logoURL,
    primaryColor: pdfData.primaryColor,
    secondaryColor: pdfData.secondaryColor,
    companyOverview: pdfData.companyOverview,
    aboutUs: pdfData.aboutUs,
    vision: pdfData.vision,
    mission: pdfData.mission,
    teamMembers: pdfData.teamMembers,
    proposalTeamMembers: pdfData.proposalTeamMembers,
    apiResponse: pdfData.apiResponse, // This contains the policy/quote data!
    proposalBreakdownDescriptions: pdfData.proposalBreakdownDescriptions,
    proposalComparisonDescriptions: pdfData.proposalComparisonDescriptions,
    documentTypeTitles: pdfData.documentTypeTitles,
    videoUrl: pdfData.videoUrl,
    acceptanceFormDescription: pdfData.acceptanceFormDescription,
    selectedTeamMember: pdfData.selectedTeamMember,
    selectedSignatureOption: pdfData.selectedSignatureOption,
    signatureImage: pdfData.signatureImage,
    extraDocumentList: pdfData.extraDocumentList,
    templateImages: pdfData.templateImages,
    dynamicLinks: pdfData.dynamicLinks || [],
  };

  let docEl: React.ReactElement;
  if (templateNum === 1) {
    const { default: ProposalDocumentT1 } = await import('./PDF/Template-1/Proposaldocumentt1');
    docEl = React.createElement(ProposalDocumentT1, pdfProps);
  } else if (templateNum === 2) {
    const { default: ProposalDocumentT2 } = await import('./PDF/Template-2/Proposaldocumentt2');
    docEl = React.createElement(ProposalDocumentT2, pdfProps);
  } else {
    const { default: ProposalDocumentMT } = await import('./PDF/Template-3/ProposalDocumentMT');
    docEl = React.createElement(ProposalDocumentMT, pdfProps);
  }
  return pdf(docEl as any).toBlob();
}

async function mergeWithExtraDocsFromSnapshot(mainBlob: Blob, extraDocumentList: Array<{ FileURL: string; FileName: string }>): Promise<Blob> {
  try {
    const { PDFDocument } = await import('pdf-lib');
    const merged = await PDFDocument.create();
    const main = await PDFDocument.load(await mainBlob.arrayBuffer());
    const pages = await merged.copyPages(main, main.getPageIndices());
    pages.forEach(p => merged.addPage(p));

    for (const doc of extraDocumentList) {
      if (!doc.FileURL) continue;
      try {
        const res = await fetch(doc.FileURL);
        const bytes = new Uint8Array(await res.arrayBuffer());
        const extra = await PDFDocument.load(bytes);
        const ep = await merged.copyPages(extra, extra.getPageIndices());
        ep.forEach(p => merged.addPage(p));
      } catch { /* skip failed extra docs */ }
    }

    const finalBytes = await merged.save();
    return new Blob([finalBytes.buffer as ArrayBuffer], { type: 'application/pdf' });
  } catch {
    return mainBlob;
  }
}

/* ── Component ── */
const Step3_PreviewSend: React.FC<Step3Props> = (props) => {
  const { showToast } = useToast();

  /* ── Generate state ── */
  const [generating, setGenerating] = useState(true);
  const [generateProgress, setGenerateProgress] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [generatedTxId, setGeneratedTxId] = useState('');

  /* ── PDF data snapshot from GeneratedProposal ── */
  const [pdfData, setPdfData] = useState<PdfDataSnapshot | null>(null);

  /* ── Action state ── */
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [sendToAmsLoading, setSendToAmsLoading] = useState(false);
  const [sendToSignLoading, setSendToSignLoading] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [signerEmail, setSignerEmail] = useState(props.clientEmail || '');
  const [signerName, setSignerName] = useState(props.clientName || '');
  const [emailError, setEmailError] = useState('');

  /* Dynamically import GeneratedProposal */
  const [GeneratedProposal, setGeneratedProposal] = useState<React.ComponentType<any> | null>(null);

  useEffect(() => {
    import('./GeneratedProposal').then(mod => {
      setGeneratedProposal(() => mod.default);
    }).catch(() => setGeneratedProposal(null));
  }, []);

  /* ── Handle PDF data ready callback from GeneratedProposal ── */
  const handlePdfDataReady = useCallback((snapshot: PdfDataSnapshot) => {
    setPdfData(snapshot);
  }, []);

  useEffect(() => {
    setSignerEmail(props.clientEmail || '');
    setSignerName(props.clientName || '');
  }, [props.clientEmail, props.clientName]);

  const handle401 = useCallback((err: any) => {
    if (axios.isAxiosError(err) && err.response?.status === 401) handleUnauthorized();
  }, []);

  /* ═══════════════════════════════════════════════════
     GENERATE PROPOSAL — called on mount
  ═══════════════════════════════════════════════════ */
  const generateProposal = useCallback(async () => {
    props.onGenerating?.();
    setGenerating(true);
    setGenerateProgress(0);
    setProcessing(false);

    const data = new FormData();

    /* Quote details — supports multiple quotes.
       selectedQuotesList[0] is always null (primary quote lives in selectedQuote).
       Always include the primary quote first, then append non-null additional slots. */
    const quoteEntries: { q: any; origIdx: number }[] = [
      ...(props.selectedQuote ? [{ q: props.selectedQuote, origIdx: 0 }] : []),
      ...(props.selectedQuotesList ?? [])
        .map((q, i) => ({ q, origIdx: i }))
        .filter(e => e.origIdx > 0 && e.q != null),
    ];
    if (quoteEntries.length > 0) {
      quoteEntries.forEach(({ q, origIdx }) => {
        const isFirst = origIdx === 0;
        const qObj = {
          TotalPremium: q.totalPremium ?? '',
          PolicyNumber: q.policyNumber ?? '',
          DatabaseId: q.databaseId ?? '',
          CarrierName: q.carrierName ?? '',
          Term: q.policyTermInMonths ?? '',
          EffectiveDate: q.effectiveDate ?? '',
          ExpirationDate: q.expirationDate ?? '',
          DocumentType: Array.isArray(q.linesOfBusiness) && q.linesOfBusiness.length > 0 ? q.linesOfBusiness[0] : '',
          CoverageDetails: isFirst ? (props.coverageDetails || []) : (props.quoteCoverageDetails?.[origIdx] || []),
          DriverList: isFirst ? (props.driverList || []) : (props.quoteDriverLists?.[origIdx] || []),
          VehicleList: isFirst ? (props.vehicleList || []) : (props.quoteVehicleLists?.[origIdx] || []),
          QuoteId: q.databaseId ?? '',
          PolicyId: isFirst ? (props.selectedPolicy?.databaseId || null) : (props.selectedPoliciesList?.[origIdx]?.databaseId || null),
        };
        data.append('quoteDetails', JSON.stringify(qObj));
      });
    }
    /* No quote selected → omit quoteDetails field entirely (empty string causes backend issues) */

    /* Collect raw policy objects — primary first, then every additional slot policy.
       Built independently of quoteEntries (same logic as draft) so all selected
       policies are always included regardless of quote selection state. */
    const policiesToSend: any[] = [
      props.selectedPolicy,
      ...(props.selectedPoliciesList ?? []).slice(1),
    ].filter(Boolean);

    data.append('documentType', '');
    data.append('templateId', props.selectedTemplate);
    data.append('videoUrl', props.videoUrl || '');
    /* Append each policy as a separate form field in the format the backend expects */
    policiesToSend.forEach(p => {
      const lob = p.Line_Of_Business ?? p.lineOfBusiness
        ?? (Array.isArray(p.linesOfBusiness) && p.linesOfBusiness.length > 0 ? p.linesOfBusiness[0] : '')
        ?? '';
      data.append('policyDetails', JSON.stringify({
        Total_Premium:   p.Total_Premium   ?? p.totalPremium   ?? p.TotalPremium   ?? 0,
        Effective_Date:  p.Effective_Date   ?? p.effectiveDate  ?? p.EffectiveDate  ?? '',
        Policy_Number:   p.Policy_Number   ?? p.policyNumber   ?? p.PolicyNumber   ?? '',
        Line_Of_Business: lob,
        Carrier_Name:    p.Carrier_Name    ?? p.carrierName    ?? p.CarrierName    ?? '',
        Term:            p.Term            ?? p.policyTermInMonths ?? 0,
        Expiration_Date: p.Expiration_Date ?? p.expirationDate ?? p.ExpirationDate ?? '',
        client_Name:     props.clientName  || '',
        client_Email:    props.clientEmail || '',
      }));
    });
    data.append('draftTransactionId', props.transactionId);

    /* File names */
    if (props.fileNames && props.fileNames.length > 0) {
      const uniqueNames = props.fileNames.filter((n, i, a) => a.indexOf(n) === i);
      uniqueNames.forEach(n => data.append('fileNames', n));
    } else {
      data.append('fileNames', '');
    }

    /* File IDs */
    if (props.fileIds && props.fileIds.length > 0) {
      const uniqueIds = props.fileIds.filter((id, i, a) => a.indexOf(id) === i);
      uniqueIds.forEach(id => data.append('fileIds', id));
    } else {
      data.append('fileIds', '');
    }

    /* Uploaded quote letter URL */
    data.append('fileURL', props.fileURL || '');

    /* Drive files — primary slot (client profile tab) */
    if (props.isDriveSelected && props.selectedDriveData && props.selectedDriveData.fileIds.length > 0) {
      data.append('amsFileIds', props.selectedDriveData.fileIds.join(','));
      data.append('folderId', props.selectedDriveData.folderId);
      data.append('nc_token', getAmsToken());
      data.append('insuredId', props.clientId);
      props.selectedDriveData.fileIds.forEach(id => data.append('driveFileIds', id));
      props.selectedDriveData.folderIds.forEach(fid => data.append('driveFolderIds', fid));
    }

    /* Drive files — additional slots (client profile on extra quote panels) */
    if (props.additionalSlotDrives && props.additionalSlotDrives.length > 0) {
      if (!props.isDriveSelected) {
        data.append('nc_token', getAmsToken());
        data.append('insuredId', props.clientId);
      }
      props.additionalSlotDrives.forEach(sd => {
        sd.driveData.fileIds.forEach(id => data.append('driveFileIds', id));
        sd.driveData.folderIds.forEach(fid => data.append('driveFolderIds', fid));
      });
    }

    data.append('clientName', props.clientName);
    data.append('clientEmail', props.clientEmail || '');
    data.append('proposalTitle', props.proposalTitle);
    data.append('selectedTeamMember', props.teamMember || '');
    data.append('extraDocDetails', JSON.stringify(props.extraDocumentList || []));
    data.append('selectedProposalMembers', JSON.stringify(props.proposalTeamMembers || []));
    data.append('clientId', props.clientId);
    data.append('customBlockDetails', JSON.stringify(props.customBlocks || []));
    data.append('dynamicLinks', JSON.stringify(props.dynamicLinks || []));

    try {
      const res = await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCQuoteProposal/generate?origin=true`,
        data,
        {
          headers: { Authorization: `Bearer ${getToolboxToken()}` },
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            setGenerateProgress(pct);
            if (pct >= 100) setProcessing(true);
          },
        }
      );
      const txId = res.data?.transactionId || props.transactionId;
      setGeneratedTxId(txId);
    } catch (err) {
      handle401(err);
      showToast('error', 'Failed to generate proposal');
      /* Fall back to draft transactionId so we show whatever was saved */
      setGeneratedTxId(props.transactionId);
    } finally {
      setGenerating(false);
      setGenerateProgress(0);
      setProcessing(false);
    }
  }, [props, handle401, showToast]);

  /* Run generate on mount */
  useEffect(() => {
    if (props.transactionId) {
      generateProposal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.transactionId]);

  /* Notify parent once proposal is ready */
  useEffect(() => {
    if (!generating && generatedTxId) {
      props.onGenerated?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generating, generatedTxId]);

  /* ── COPY ── */
  const handleCopy = useCallback(() => {
    const link = `${window.location.origin}/proposal-view/${generatedTxId || props.transactionId}`;
    navigator.clipboard.writeText(link).then(() => {
      showToast('success', 'Proposal link copied to clipboard');
    }).catch(() => showToast('error', 'Failed to copy link'));
  }, [generatedTxId, props.transactionId, showToast]);

  /* ── SHARE ── copy link + open in new tab ── */
  const handleShare = useCallback(() => {
    const link = `${window.location.origin}/proposal-view/${generatedTxId || props.transactionId}`;
    navigator.clipboard.writeText(link).catch(() => {});
    window.open(link, '_blank', 'noopener,noreferrer');
    showToast('success', 'Link copied & opened in new tab');
  }, [generatedTxId, props.transactionId, showToast]);

  /* ── DOWNLOAD ── */
  const handleDownloadPDF = useCallback(async () => {
    if (!pdfData) {
      showToast('error', 'PDF data not ready. Please wait for preview to load.');
      return;
    }
    setIsGeneratingPdf(true);
    try {
      const mainBlob = await buildPdfBlobFromSnapshot(pdfData, props.selectedTemplate);
      const finalBlob = await mergeWithExtraDocsFromSnapshot(mainBlob, pdfData.extraDocumentList || []);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(finalBlob);
      link.download = `Proposal-${generatedTxId || props.transactionId}.pdf`;
      link.click();
      showToast('success', 'Download started');
    } catch (err) {
      console.error('PDF generation failed:', err);
      showToast('error', 'Failed to generate PDF');
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [pdfData, generatedTxId, props.transactionId, props.selectedTemplate, showToast]);

  /* ── SEND TO AMS ── */
  const handleSendToAms = useCallback(async () => {
    if (!pdfData) {
      showToast('error', 'PDF data not ready. Please wait for preview to load.');
      return;
    }
    if (!props.transactionId || !props.clientId) {
      showToast('error', 'Missing required information'); return;
    }
    setSendToAmsLoading(true);
    setIsGeneratingPdf(true);
    try {
      const mainBlob = await buildPdfBlobFromSnapshot(pdfData, props.selectedTemplate);
      const finalBlob = await mergeWithExtraDocsFromSnapshot(mainBlob, pdfData.extraDocumentList || []);
      const uid = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString();
      const file = new File([finalBlob], `Quote_Proposal_${uid}.pdf`, { type: 'application/pdf' });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', file.name);
      formData.append('nc_token', getAmsToken());
      formData.append('insuredId', props.clientId);
      formData.append('transactionId', generatedTxId || props.transactionId);

      await axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCQuoteProposal/sendtoams`, formData, {
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      });
      showToast('success', 'Sent to AMS successfully');
    } catch (err) {
      handle401(err);
      showToast('error', 'Failed to send to AMS');
    } finally {
      setSendToAmsLoading(false);
      setIsGeneratingPdf(false);
    }
  }, [pdfData, generatedTxId, props.transactionId, props.clientId, props.selectedTemplate, handle401, showToast]);

  /* ── SIGN ── */
  const handleSignDocument = useCallback(async () => {
    if (!pdfData) {
      showToast('error', 'PDF data not ready. Please wait for preview to load.');
      return;
    }
    setSendToSignLoading(true);
    setIsGeneratingPdf(true);
    try {
      const mainBlob = await buildPdfBlobFromSnapshot(pdfData, props.selectedTemplate);
      const finalBlob = await mergeWithExtraDocsFromSnapshot(mainBlob, pdfData.extraDocumentList || []);
      const uid = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString();
      const file = new File([finalBlob], `Quote_Proposal_${uid}.pdf`, { type: 'application/pdf' });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', file.name);
      formData.append('nc_token', getAmsToken());
      formData.append('insuredId', props.clientId);
      formData.append('transactionId', generatedTxId || props.transactionId);

      const uploadRes = await axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/NCQuoteProposal/sendtoams`, formData, {
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      });
      const databaseId = uploadRes.data?.data?.databaseId || uploadRes.data?.databaseId;

      const tokenRes = await axios.get(`${(process.env.REACT_APP_Nowcerts_Base_Url || '')}/api/IGToken/GetToken`, {
        headers: { Authorization: `Bearer ${getAmsToken()}` },
      });
      const raw = tokenRes.data;
      const igtoken: string = typeof raw === 'string'
        ? raw
        : (raw?.token ?? raw?.igToken ?? raw?.Token ?? raw?.IGToken ?? String(raw ?? ''));
      if (!igtoken) throw new Error('Unable to generate auth token for signing');

      const fromEmail = localStorage.getItem('userName') || '';
      const url =
        `${(process.env.REACT_APP_RabbitSign_Url || 'https://test-bff-api.nowcerts.com')}/RabbitSignExternal` +
        `?igtoken=${igtoken}` +
        '&theme=dark' +
        `&fileId=${databaseId}` +
        `&insuredId=${props.clientId}` +
        `&signers=${encodeURIComponent(signerEmail)},${encodeURIComponent(signerName)},1` +
        `&fromEmail=${encodeURIComponent(fromEmail)}` +
        '&emailSubject=Please%20sign%20this%20document' +
        '&emailBody=Please%20sign%20this%20document';
      window.open(url, '_blank');
      showToast('success', 'Document sent for signing');
    } catch (err) {
      handle401(err);
      showToast('error', 'Failed to prepare document for signing');
    } finally {
      setSendToSignLoading(false);
      setIsGeneratingPdf(false);
      setShowEmailDialog(false);
    }
  }, [pdfData, generatedTxId, props.transactionId, props.clientId, props.selectedTemplate, signerEmail, signerName, handle401, showToast]);

  const handleConfirmSign = useCallback(() => {
    if (!signerEmail.trim()) { setEmailError('Email is required'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(signerEmail)) { setEmailError('Please enter a valid email'); return; }
    setEmailError('');
    handleSignDocument();
  }, [signerEmail, handleSignDocument]);

  const anyLoading = isGeneratingPdf || sendToAmsLoading || sendToSignLoading;

  return (
    <>
      {/* ── Preview section ── */}
      <div className="qp-section glass-surface">

        {/* ── Premium generation loader ── */}
        {generating && (() => {
          const p = generateProgress;
          const steps = [
            { label: 'Analyzing client & quote data',    at: 0  },
            { label: 'Assembling proposal template',      at: 22 },
            { label: 'Applying AI enhancements',          at: 50 },
            { label: 'Finalizing & packaging document',   at: 78 },
          ];
          const effectiveP = processing ? 95 : p > 0 ? p : 0;
          return (
            <div className="qp-gen-loader">
              {/* ── Orbital orb ── */}
              <div className="qp-gen-loader__orb">
                <div className="qp-gen-loader__ring qp-gen-loader__ring--1" />
                <div className="qp-gen-loader__ring qp-gen-loader__ring--2" />
                <div className="qp-gen-loader__ring qp-gen-loader__ring--3" />
                <div className="qp-gen-loader__core">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#fff' }}>
                    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
                  </svg>
                </div>
              </div>

              {/* ── Step checklist ── */}
              <div className="qp-gen-loader__steps">
                {steps.map((s, i) => {
                  const done   = effectiveP >= s.at + 22;
                  const active = !done && effectiveP >= s.at;
                  return (
                    <div key={i} className={`qp-gen-loader__step${done ? ' qp-gen-loader__step--done' : active ? ' qp-gen-loader__step--active' : ''}`}>
                      <span className="qp-gen-loader__step-icon">
                        {done
                          ? <FiCheck size={10} />
                          : active
                            ? <span className="qp-gen-loader__step-pulse" />
                            : <span className="qp-gen-loader__step-idle" />}
                      </span>
                      <span className="qp-gen-loader__step-label">{s.label}</span>
                    </div>
                  );
                })}
              </div>

              {/* ── Progress track ── */}
              <div className="qp-gen-loader__bar-wrap">
                <div className="qp-gen-loader__bar-track">
                  {effectiveP > 0
                    ? <div className="qp-gen-loader__bar-fill" style={{ width: `${effectiveP}%` }} />
                    : <div className="qp-gen-loader__bar-indeterminate" />}
                </div>
                <div className="qp-gen-loader__bar-label">
                  {processing
                    ? 'Finalizing…'
                    : effectiveP > 0
                      ? `${effectiveP}%`
                      : 'Generating Proposal…'}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Action bar + preview — shown once generated */}
        {!generating && generatedTxId && (
          <>
            <div className="qp-preview-header">
              <div>
                <h3 className="qp-section__title">Preview &amp; Send</h3>
                <p className="qp-section__subtitle" style={{ marginBottom: 0 }}>
                  Review the AI-generated proposal and send or download
                </p>
              </div>
              <div className="qp-preview-actions">
                <button className="qp-pill-btn qp-pill-btn--outline" onClick={handleCopy} disabled={anyLoading} title="Copy link">
                  <FiCopy size={12} /> COPY
                </button>
                <button className="qp-pill-btn qp-pill-btn--outline" onClick={handleDownloadPDF} disabled={anyLoading} title="Download PDF">
                  {isGeneratingPdf && !sendToAmsLoading && !sendToSignLoading
                    ? <div className="qp-spinner qp-spinner--small" />
                    : <FiDownload size={12} />}
                  DOWNLOAD PDF
                </button>
                <button className="qp-pill-btn qp-pill-btn--outline" onClick={handleSendToAms} disabled={anyLoading} title="Send to AMS">
                  {sendToAmsLoading ? <div className="qp-spinner qp-spinner--small" /> : <FiSend size={12} />}
                  SEND TO AMS
                </button>
                <button className="qp-pill-btn qp-pill-btn--outline" onClick={() => setShowEmailDialog(true)} disabled={anyLoading} title="Sign document">
                  {sendToSignLoading ? <div className="qp-spinner qp-spinner--small" /> : <FiEdit2 size={12} />}
                  SIGN DOCUMENT
                </button>
              </div>
            </div>

            <motion.div
              className="qp-preview-container"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {GeneratedProposal ? (
                <GeneratedProposal
                  transactionId={generatedTxId}
                  selectedTemplate={props.selectedTemplate}
                  isEditable={true}
                  isGeneratingPdf={isGeneratingPdf}
                  onPdfDataReady={handlePdfDataReady}
                  proposalTeamMembers={props.proposalTeamMembers || []}
                  customBlocksData={props.customBlocks || []}
                  dynamicLinksData={props.dynamicLinks || []}
                  videoUrl={props.videoUrl || ''}
                  onNewProposal={props.onNewProposal}
                  showBottomActions={false}
                />
              ) : (
                <ProposalPreviewFallback
                  title={props.proposalTitle}
                  clientName={props.clientName}
                  companyData={props.companyData}
                  fileIds={props.fileIds}
                  proposalTeamMembers={props.proposalTeamMembers}
                  customBlocks={props.customBlocks}
                />
              )}
            </motion.div>

            {/* Share Proposal + Create New */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 0 4px' }}>
              <button
                className="glass-btn glass-btn-primary"
                onClick={handleShare}
                disabled={anyLoading}
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <FiSend size={14} /> Share Proposal
              </button>
              <button
                className="glass-btn"
                onClick={() => props.onNewProposal?.()}
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                + Create New
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── Sign dialog ── */}
      {showEmailDialog && (
        <div className="qp-dialog-overlay" onClick={() => setShowEmailDialog(false)}>
          <motion.div
            className="qp-dialog"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="qp-dialog__header">
              <h3>Sign Document</h3>
              <button className="qp-dialog__close" onClick={() => setShowEmailDialog(false)}>
                <FiX size={18} />
              </button>
            </div>
            <div className="qp-dialog__body">
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 'var(--space-4)' }}>
                The document will be sent to this email for e-signature.
              </p>
              <div className="qp-field">
                <label className="qp-field__label">Email *</label>
                <input
                  type="email"
                  className={`qp-field__input${emailError ? ' qp-field__input--error' : ''}`}
                  placeholder="Enter signer email"
                  value={signerEmail}
                  onChange={e => { setSignerEmail(e.target.value); setEmailError(''); }}
                />
                {emailError && <span className="qp-field__error">{emailError}</span>}
              </div>
            </div>
            <div className="qp-dialog__footer">
              <button className="glass-btn" onClick={() => setShowEmailDialog(false)} disabled={sendToSignLoading}>Cancel</button>
              <button className="glass-btn glass-btn-primary" onClick={handleConfirmSign} disabled={sendToSignLoading}>
                {sendToSignLoading ? <div className="qp-spinner qp-spinner--small" /> : <FiCheck size={13} />} Confirm &amp; Sign
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

/* ── Fallback preview ── */
const ProposalPreviewFallback: React.FC<{
  title: string; clientName: string; companyData: any;
  fileIds: string[]; proposalTeamMembers: any[]; customBlocks: any[];
}> = ({ title, clientName, companyData, fileIds, proposalTeamMembers, customBlocks }) => (
  <div className="qp-preview-doc">
    <div className="qp-preview-doc__header">
      {companyData?.logoUrl || companyData?.logoURL ? (
        <img src={companyData.logoUrl || companyData.logoURL} alt="Logo" className="qp-preview-doc__logo" />
      ) : (
        <div className="qp-preview-doc__logo-placeholder" style={{ background: companyData?.primaryColour || '#192784' }}>
          {(companyData?.name || 'C').charAt(0)}
        </div>
      )}
    </div>
    <h2 className="qp-preview-doc__title">{title || 'Insurance Proposal'}</h2>
    <p className="qp-preview-doc__prepared">Prepared for <strong>{clientName || 'Client'}</strong></p>
    <div className="qp-preview-doc__divider" />
    {companyData?.overview && (
      <div className="qp-preview-doc__section"><h4>Overview</h4><p>{companyData.overview}</p></div>
    )}
    {proposalTeamMembers.length > 0 && (
      <div className="qp-preview-doc__section">
        <h4>Your Team</h4>
        <div className="qp-preview-doc__team">
          {proposalTeamMembers.map((m: any, i: number) => (
            <div key={i} className="qp-preview-doc__team-member">
              <div className="qp-preview-doc__team-avatar">{(m.name || '?').charAt(0)}</div>
              <div>
                <span className="qp-preview-doc__team-name">{m.name}</span>
                {m.role && <span className="qp-preview-doc__team-role">{m.role}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
    {customBlocks.filter((b: any) => b.visible).map((b: any) => (
      <div key={b.id} className="qp-preview-doc__section">
        <h4>{b.title}</h4>
        <p>{b.content}</p>
      </div>
    ))}
    {fileIds.length > 0 && (
      <div className="qp-preview-doc__section">
        <h4>Attached Documents</h4>
        <p>{fileIds.length} document(s) attached</p>
      </div>
    )}
    <div className="qp-preview-doc__footer">
      <div className="qp-preview-doc__divider" />
      <p style={{ fontSize: 12, color: '#94a3b8' }}>Generated with Quote Proposal · {new Date().toLocaleDateString()}</p>
    </div>
  </div>
);

export default React.memo(Step3_PreviewSend);

